import {
  AssetEligibility,
  AssetExitKind,
  FindingDto,
  FindingLevel,
  PartnerBadge,
  PartnerIncomeTaxDocumentKind,
  PartnerIncomeTaxDocumentStatus,
  ReturnSummaryDto,
  ValueSource,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';

/**
 * Formatação da aba IR dos sócios (card 3515). Só exibição: todo número chega pronto do backend.
 *
 * Convenção de unidade (alinhada com o protótipo do Bruno): KPIs e frases em "R$ 15,5 MM" / "R$ 620 mil";
 * tabelas em R$ mil sem o prefixo ("16.155"). Os valores chegam em reais.
 *
 * Convenção de percentual: `sharePercent` vem de 0 a 100 (participação no capital, como no contrato social);
 * todas as outras razões (`netWorthYoY`, `cagr`, `*Pct`, `debtsOverNetWorth`, `exposureToBorrower`, `coverage`,
 * `haircut`) vêm como fração (0,221 = 22,1%).
 */

const EMPTY = '—';

function round(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function formatNumber(value: number, digits = 0): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * "R$ 15,5 MM" (a partir de R$ 1 milhão) ou "R$ 620 mil". `sign` põe "+" nos positivos.
 * Abaixo de R$ 1 mil mostra "R$ 0 mil" (ordem de grandeza do painel, não centavos).
 */
export function formatCompactMoney(value: number | null | undefined, sign = false): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  const abs = Math.abs(value);
  const body =
    abs >= 1_000_000
      ? `R$ ${formatNumber(round(abs / 1_000_000, 1), 1)} MM`
      : `R$ ${formatNumber(Math.round(abs / 1_000))} mil`;
  if (value < 0 && Math.round(abs / 1_000) > 0) return `-${body}`;
  return sign && value > 0 ? `+${body}` : body;
}

/** Valor de tabela em R$ mil: "16.155". Nulo vira "—". */
export function formatThousands(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  return formatNumber(Math.round(value / 1_000));
}

/** Abaixo disto (em reais) o valor só se lê direito em R$ mil com uma casa ("0,4" em vez de "0"). */
const FINE_VALUE_LIMIT = 10_000;

/**
 * Casas decimais da tabela de bens inteira: 1 quando algum valor diferente de zero fica abaixo de R$ 10 mil, senão 0.
 * A tabela usa uma grafia só: com a casa decidida valor a valor, "5,0" aparecia ao lado de "256" (Bruno, 30/10/2026).
 */
export function fineThousandsDigits(values: readonly (number | null | undefined)[]): 0 | 1 {
  return values.some(
    value =>
      value != null && Number.isFinite(value) && value !== 0 && Math.abs(value) < FINE_VALUE_LIMIT
  )
    ? 1
    : 0;
}

/**
 * Valor da tabela de bens em R$ mil, com as casas que a tabela inteira usa (`fineThousandsDigits`): com uma casa,
 * "0,4", "256,0", "16.155,0"; sem, "256", "16.155". A casa existe porque a conta de R$ 400 aparecia "0" ao lado de
 * uma variação que não batia com o que estava na tela. Zero segue as casas da tabela ("0,0" ou "0"); valor que não
 * é zero mas arredonda para 0,0 vira "< 0,1", para não parecer zero; nulo, "—".
 */
export function formatThousandsFine(value: number | null | undefined, digits: 0 | 1 = 1): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  if (value === 0) return formatNumber(0, digits);
  if (digits === 0) return formatThousands(value);
  const rounded = round(value / 1_000, 1);
  if (rounded === 0) return value > 0 ? '< 0,1' : '> -0,1';
  return formatNumber(rounded, 1);
}

/** Variação em R$ mil com sinal: "+1.200", "-140"; zero ou nulo vira "—". */
export function formatThousandsSigned(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  const rounded = Math.round(value / 1_000);
  if (rounded === 0) return EMPTY;
  return `${rounded > 0 ? '+' : '-'}${formatNumber(Math.abs(rounded))}`;
}

/**
 * Fração em percentual: 0,221 → "22,1%". `sign` põe "+" nos positivos. Nulo vira "—".
 */
export function formatRatio(ratio: number | null | undefined, digits = 0, sign = false): string {
  if (ratio == null || !Number.isFinite(ratio)) return EMPTY;
  const pct = round(ratio * 100, digits);
  const body = `${formatNumber(Math.abs(pct), digits)}%`;
  if (pct < 0) return `-${body}`;
  return sign && pct > 0 ? `+${body}` : body;
}

/**
 * Nota do KPI de patrimônio: "+6,2% no ano · +4,1% a.a. desde 2022". A média anual some quando repetiria o número
 * do ano (janela de 2 anos, ou o mesmo texto depois de arredondar) (A6).
 */
export function describeNetWorthChange(
  yoy: number | null,
  cagr: number | null,
  firstYear: number | null,
  windowSize: number
): string {
  const parts: string[] = [];
  const yearText = yoy != null ? formatRatio(yoy, 1, true) : null;
  if (yearText) parts.push(`${yearText} no ano`);
  if (cagr != null) {
    const cagrText = formatRatio(cagr, 1, true);
    const repeats = !!yearText && (windowSize <= 2 || cagrText === yearText);
    if (!repeats) parts.push(`${cagrText} a.a.${firstYear ? ` desde ${firstYear}` : ''}`);
  }
  return parts.join(' · ');
}

/**
 * Faixa de dívidas ÷ PL pelos limites da Parametrização, só para a cor do KPI do consolidado quando o grupo não tem
 * ponto IRS-D1 (N17). Mesma leitura do backend: Crítico acima de `critical`, Atenção a partir de `attention`, OK
 * abaixo de `ok`; entre OK e Atenção, sem nível. Sem valor ou sem limites, `null`.
 */
export function debtLevelFromLimits(
  ratio: number | null | undefined,
  limits: { ok: number; attention: number; critical: number } | null | undefined
): FindingLevel | null {
  if (ratio == null || !Number.isFinite(ratio) || !limits) return null;
  if (ratio > limits.critical) return 'critico';
  if (ratio >= limits.attention) return 'atencao';
  if (ratio < limits.ok) return 'ok';
  return null;
}

/** Participação no capital (0 a 100): "45%" ou "12,5%". */
export function formatSharePercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return EMPTY;
  const digits = Number.isInteger(round(value, 1)) ? 0 : 1;
  return `${formatNumber(round(value, 1), digits)}%`;
}

/** Tom de um número com sinal: positivo verde, negativo vermelho, zero/nulo neutro. */
export type Tone = 'positive' | 'negative' | 'neutral';

export function signTone(value: number | null | undefined): Tone {
  if (value == null || !Number.isFinite(value) || value === 0) return 'neutral';
  return value > 0 ? 'positive' : 'negative';
}

/** Rótulo e classe do selo de cada nível (cog-tag: Crítico vermelho, Atenção amarelo, Informativo azul, OK verde). */
export const FINDING_LEVEL_TAGS: Record<FindingLevel, { label: string; css: string }> = {
  critico: { label: 'Crítico', css: 'pit-tag--danger' },
  atencao: { label: 'Atenção', css: 'pit-tag--warning' },
  informativo: { label: 'Informativo', css: 'pit-tag--info' },
  ok: { label: 'OK', css: 'pit-tag--success' },
};

export const ELIGIBILITY_TAGS: Record<AssetEligibility, { label: string; css: string }> = {
  elegivel: { label: 'Elegível', css: 'pit-tag--success' },
  restrito: { label: 'Restrito', css: 'pit-tag--warning' },
  inelegivel: { label: 'Inelegível', css: 'pit-tag--neutral' },
  vendido: { label: 'Vendido', css: 'pit-tag--neutral' },
};

export const PARTNER_BADGE_TAGS: Record<PartnerBadge, { label: string; css: string }> = {
  compativel: { label: 'Compatível', css: 'pit-tag--success' },
  critico: { label: 'Crítico', css: 'pit-tag--danger' },
  atencao: { label: 'Atenção', css: 'pit-tag--warning' },
  'sem-declaracao': { label: 'Sem declaração', css: 'pit-tag--neutral' },
  // Mesmo termo do selo da lista e do aviso de andamento (card 3773).
  processando: { label: 'Lendo', css: 'pit-tag--info' },
};

export const VALUE_SOURCE_LABELS: Record<ValueSource, string> = {
  laudo: 'Laudo',
  extrato: 'Extrato',
  declarado: 'Declarado',
  analista: 'Estimado',
};

/** Selo do status de leitura de um documento do Data Room (Lida verde, Não lida cinza, Com erro vermelho, fila azul). */
export const DOCUMENT_STATUS_TAGS: Record<
  PartnerIncomeTaxDocumentStatus,
  { label: string; css: string; busy: boolean; icon?: string }
> = {
  read: { label: 'Lida', css: 'pit-tag--success', busy: false },
  notRead: { label: 'Não lida', css: 'pit-tag--neutral', busy: false },
  failed: { label: 'Com erro', css: 'pit-tag--danger', busy: false },
  // Fila parada não gira: relógio estático; o spinner fica só para quem o agente está lendo (card 3772).
  queued: { label: 'Na fila', css: 'pit-tag--info', busy: false, icon: 'pi pi-clock' },
  processing: { label: 'Lendo', css: 'pit-tag--info', busy: true },
};

export const DOCUMENT_KIND_LABELS: Record<PartnerIncomeTaxDocumentKind, string> = {
  declaracao: 'Declaração',
  recibo: 'Recibo',
  apoio: 'Documento de apoio',
};

/** Documento que ainda pode ser mandado para leitura: o que está na fila ou sendo lido, não. */
export function canSelectDocument(document: { status: PartnerIncomeTaxDocumentStatus }): boolean {
  return document.status !== 'queued' && document.status !== 'processing';
}

/** Resumo da lista de documentos: "5 documentos lidos" ou "5 documentos · 3 lidos · 2 não lidos · 1 com erro". */
export function describeDocumentsSummary(
  docs: readonly { status: PartnerIncomeTaxDocumentStatus }[]
): string {
  if (!docs.length) return '';
  const count = (status: PartnerIncomeTaxDocumentStatus) =>
    docs.filter(doc => doc.status === status).length;
  const read = count('read');
  if (read === docs.length) {
    return `${docs.length} ${docs.length === 1 ? 'documento lido' : 'documentos lidos'}`;
  }
  const notRead = count('notRead');
  const busy = count('queued') + count('processing');
  return [
    `${docs.length} ${docs.length === 1 ? 'documento' : 'documentos'}`,
    read ? `${read} ${read === 1 ? 'lido' : 'lidos'}` : null,
    notRead ? `${notRead} ${notRead === 1 ? 'não lido' : 'não lidos'}` : null,
    count('failed') ? `${count('failed')} com erro` : null,
    busy ? `${busy} em leitura` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * "Extrair dados (3)". Antes era "Ler selecionados", que se confundia com abrir o documento (revisão de 29/10/2026,
 * card 3799): o botão manda o Cog extrair de novo os dados do PDF e atualizar o sócio.
 */
export function describeReadSelectedLabel(count: number): string {
  return count ? `Extrair dados (${count})` : 'Extrair dados';
}

/**
 * Toast depois do "Extrair dados": quantos foram para a leitura e, à parte, os que o backend recusou com o motivo
 * dele ("Já está sendo lido."), com o nome do arquivo quando a tela o conhece.
 */
export function describeReadResult(
  response: { queued: number; skipped: readonly { documentId: string; reason: string }[] },
  fileNames: ReadonlyMap<string, string>
): { success: string | null; skipped: string | null } {
  const queued = response.queued;
  const success = queued
    ? `${queued} ${queued === 1 ? 'documento enviado' : 'documentos enviados'} para leitura. A lista e a aba se atualizam sozinhas quando a leitura terminar.`
    : null;
  const skipped = response.skipped.length
    ? response.skipped
        .map(item => `${fileNames.get(item.documentId) ?? 'Documento'}: ${item.reason}`)
        .join(' ')
    : null;
  return { success, skipped };
}

/**
 * Conclusão do herói do consolidado: o pior nível entre os sócios (pelo selo que cada um já traz no overview) e de
 * onde vem. Ex.: "1 sócio em nível Crítico: Fulano Silva: Dívidas somam 406% do PL". Nada é calculado: é o selo e a
 * conclusão de cada sócio. Sem Crítico nem Atenção: "Nenhum ponto de atenção nos sócios" (com quantos ainda não têm
 * declaração lida, quando houver). Os pontos do GRUPO ficam no card próprio.
 */
export function describeGroupConclusion(
  partners: readonly { name: string; badge: PartnerBadge; conclusion?: string | null }[]
): { tagCss: string | null; tagLabel: string | null; text: string } {
  for (const badge of ['critico', 'atencao'] as const) {
    const hits = partners.filter(partner => partner.badge === badge);
    if (!hits.length) continue;
    const tag = PARTNER_BADGE_TAGS[badge];
    const who = hits
      .map(partner => {
        const name = formatPersonName(partner.name);
        return partner.conclusion ? `${name}: ${partner.conclusion}` : name;
      })
      .join('; ');
    const count = `${hits.length} ${hits.length === 1 ? 'sócio' : 'sócios'}`;
    // " · " separa o resumo de quem: com ":" a frase ficava com dois-pontos em sequência.
    return {
      tagCss: tag.css,
      tagLabel: tag.label,
      text: `${count} em nível ${tag.label} · ${who}`,
    };
  }
  const pending = partners.filter(
    partner => partner.badge === 'sem-declaracao' || partner.badge === 'processando'
  ).length;
  const suffix = pending
    ? ` (${pending} ${pending === 1 ? 'sócio ainda sem declaração lida' : 'sócios ainda sem declaração lida'})`
    : '';
  return {
    tagCss: pending ? null : PARTNER_BADGE_TAGS.compativel.css,
    tagLabel: pending ? null : 'OK',
    text: `Nenhum ponto de atenção nos sócios${suffix}`,
  };
}

/** Filtro da tabela de bens: todos ou uma situação. */
export type AssetFilter = 'all' | AssetEligibility;

export const ASSET_FILTERS: { key: AssetFilter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'elegivel', label: 'Elegíveis' },
  { key: 'restrito', label: 'Restritos' },
  { key: 'inelegivel', label: 'Inelegíveis' },
  // Bens que saíram da declaração (vendidos, baixados, doados...): "Vendido" só quando foi venda (N15).
  { key: 'vendido', label: 'Saíram' },
];

const EXIT_KIND_LABELS: Record<AssetExitKind, string> = {
  venda: 'Vendido',
  baixa: 'Baixado',
  doacao: 'Doado',
  transferencia: 'Transferido',
  liquidacao: 'Resgatado',
  zerado: 'Zerado',
};

/**
 * Selo da situação do bem. Bem que saiu da declaração ganha o rótulo do `exitKind` do backend ("Vendido" só para
 * venda); sem `exitKind` (resposta antiga), o rótulo antigo "Vendido".
 */
export function describeAssetSituation(asset: {
  eligibility: AssetEligibility;
  exitKind?: AssetExitKind | null;
}): { label: string; css: string; title: string } {
  const tag = ELIGIBILITY_TAGS[asset.eligibility];
  if (asset.eligibility !== 'vendido') return { ...tag, title: tag.label };
  const label = asset.exitKind ? (EXIT_KIND_LABELS[asset.exitKind] ?? tag.label) : tag.label;
  const title =
    asset.exitKind === 'zerado'
      ? 'Declarado com saldo zero no ano'
      : `${label}: saiu da declaração no ano`;
  return { label, css: tag.css, title };
}

/** Nome curto do grupo da ficha Bens e Direitos, para o agrupamento da tabela de bens. */
const ASSET_GROUP_SHORT_LABELS: Record<string, string> = {
  '01': 'Imóveis',
  '02': 'Veículos e bens móveis',
  '03': 'Participações',
  '04': 'Aplicações',
  '05': 'Créditos',
  '06': 'Contas e dinheiro',
  '07': 'Fundos',
  '08': 'Criptoativos',
  '99': 'Outros',
};

export function assetGroupLabel(group: string, fallback?: string | null): string {
  return ASSET_GROUP_SHORT_LABELS[group] ?? fallback ?? 'Outros';
}

/**
 * Siglas que continuam em maiúsculas quando o nome do bem vem todo em caixa alta do IRPF. Siglas sem vogal
 * (XP, CDB, BTG, CNPJ) e com ponto entre letras (S.A.) já são reconhecidas pela forma; aqui ficam as que têm
 * vogal e seriam confundidas com palavra.
 */
const KEPT_ACRONYMS = new Set([
  'SA',
  'LTDA',
  'EIRELI',
  'EPP',
  'ME',
  'LCA',
  'LCI',
  'CRA',
  'CRI',
  'FII',
  'FIA',
  'FIDC',
  'FIP',
  'FIM',
  'IPCA',
  'CDI',
  'DI',
  'PGBL',
  'VGBL',
  'ETF',
  'BDR',
  'COE',
  'CEF',
  'BB',
  'NTN',
]);

const VOWELS = /[AEIOUÁÉÍÓÚÂÊÔÃÕÀ]/;

function keepsUppercase(word: string, previousChar: string): boolean {
  if (KEPT_ACRONYMS.has(word.replace(/\./g, ''))) return true;
  // S.A., E.U.A.
  if (/^(\p{L}\.)+\p{L}?\.?$/u.test(word)) return true;
  // Placa, modelo, código: letras e números juntos (200CC, ABC1D23).
  if (/\p{L}/u.test(word) && /\p{N}/u.test(word)) return true;
  // UF depois de barra: "SÃO PAULO/SP".
  if (previousChar === '/' && /^\p{L}{2}$/u.test(word)) return true;
  // Sigla curta sem vogal: XP, CDB, BTG, CNPJ.
  return word.length >= 2 && word.length <= 4 && !VOWELS.test(word);
}

/**
 * Nome do bem para exibição. O IRPF costuma trazer tudo em maiúsculas ("APARTAMENTO 802 DO EDIFICIO..."): nesse
 * caso aplica capitalização de frase, preservando siglas curtas (S.A., LTDA, XP, CDB). Nome que já tem minúsculas
 * fica como veio. Só exibição: o valor gravado não muda.
 */
export function formatAssetName(name: string | null | undefined): string {
  const text = (name ?? '').trim();
  if (!text || /\p{Ll}/u.test(text)) return text;
  const sentence = text
    .replace(/[\p{L}\p{N}.]+/gu, (word, offset: number) =>
      keepsUppercase(word, offset > 0 ? text[offset - 1] : '') ? word : word.toLowerCase()
    )
    // "S/A" é sigla (o "/" separa as duas letras e cada uma sozinha não parece sigla).
    .replace(/(^|[^\p{L}])s\/a(?![\p{L}])/giu, '$1S/A');
  return sentence.replace(/\p{L}/u, letter => letter.toUpperCase());
}

/** Partículas que ficam em minúsculas no nome de pessoa ("Maria da Silva e Souza"). */
const NAME_PARTICLES = new Set(['da', 'de', 'do', 'das', 'dos', 'e']);

/**
 * Nome de pessoa para exibição. O IRPF traz o nome todo em maiúsculas ("MARIA DA SILVA"): nesse caso vira "Maria da
 * Silva", com as partículas em minúsculas. Nome que já tem minúsculas fica como veio. Só exibição.
 */
export function formatPersonName(name: string | null | undefined): string {
  const text = (name ?? '').trim().replace(/\s+/g, ' ');
  if (!text || /\p{Ll}/u.test(text)) return text;
  return text
    .toLowerCase()
    .split(' ')
    .map((word, index) =>
      index > 0 && NAME_PARTICLES.has(word)
        ? word
        : word.replace(
            /(^|[-'’])(\p{L})/gu,
            (_match, before: string, letter: string) => `${before}${letter.toUpperCase()}`
          )
    )
    .join(' ');
}

/** Variação acima disto (em fração) aparece como "> +999%". */
const CHANGE_DISPLAY_CAP = 9.99;
/** Saldo anterior abaixo disto (em reais) não serve de base para percentual: a coluna mostra "—". */
const CHANGE_MIN_BASE = 1_000;

/**
 * Coluna "Var." da tabela de bens. Regra de exibição (não é cálculo novo: o percentual vem do backend), na ordem:
 * - "novo" só quando o bem não existia no ano anterior: valor nulo naquele ano (ou zero com `isNew` do backend, para
 *   o caso de o backend preencher o ano ausente com 0), com saldo atual positivo. Nunca por o valor ser pequeno;
 * - saldo atual zero (ou ausente) com anterior positivo: "zerado", qualquer que seja o tamanho do anterior;
 * - os dois zerados ou ausentes: "—";
 * - saldo anterior abaixo de R$ 1 mil com os dois positivos: "—" (base pequena demais para percentual);
 * - percentual nulo (o backend manda nulo quando o anterior é 0 ou nulo): "—";
 * - acima de 999%: "> +999%".
 * Com só um ano na janela, vale o `isNew` do backend.
 */
export function describeAssetChange(
  asset: {
    isNew: boolean;
    lastYearChangePct: number | null;
    values: readonly { year: number; value: number | null }[];
  },
  years: readonly number[]
): { text: string; tone: Tone } {
  const none = { text: EMPTY, tone: 'neutral' as Tone };
  const isNew = { text: 'novo', tone: 'positive' as Tone };
  const last = years.length ? years[years.length - 1] : null;
  const previousYear = years.length > 1 ? years[years.length - 2] : null;
  if (previousYear == null) return asset.isNew ? isNew : none;

  const current = last == null ? null : valueForYear(asset.values, last);
  const previous = valueForYear(asset.values, previousYear);
  const currentPositive = current != null && current > 0;
  const previousPositive = previous != null && previous > 0;

  if (previous == null || (previous === 0 && asset.isNew)) return currentPositive ? isNew : none;
  if (previousPositive && !currentPositive && (current == null || current === 0)) {
    return { text: 'zerado', tone: 'negative' };
  }
  if (!previousPositive && !currentPositive) return none;
  if (Math.abs(previous) < CHANGE_MIN_BASE) return none;
  const pct = asset.lastYearChangePct;
  if (pct == null || !Number.isFinite(pct)) return { text: EMPTY, tone: 'neutral' };
  if (pct > CHANGE_DISPLAY_CAP) return { text: '> +999%', tone: 'positive' };
  return { text: formatRatio(pct, 0, true), tone: signTone(pct) };
}

/**
 * Fonte de um ponto de atenção. O backend monta o `source` começando pelos ids das regras ("IRS-D1, IRS-D2 · Dívidas e
 * Ônus 2025"); a tela mostra só a parte legível ("Fonte: Dívidas e Ônus 2025") e deixa os ids para o `title`.
 */
export function describeFindingSource(finding: Pick<FindingDto, 'source' | 'pointIds'>): {
  text: string;
  ids: string;
} {
  const source = (finding.source ?? '').trim();
  const separator = source.indexOf('·');
  const head = separator >= 0 ? source.slice(0, separator).trim() : '';
  const looksLikeIds =
    !!head && /^[A-Z]{2,4}-[A-Z]{1,2}\d+(\s*,\s*[A-Z]{2,4}-[A-Z]{1,2}\d+)*$/.test(head);
  const readable = looksLikeIds ? source.slice(separator + 1).trim() : source;
  const ids = looksLikeIds ? head : finding.pointIds.join(', ');
  return { text: readable ? `Fonte: ${readable}` : '', ids };
}

/** Quantos bens em cada filtro. */
export function countAssetsByFilter(
  assets: readonly { eligibility: AssetEligibility }[]
): Record<AssetFilter, number> {
  const counts: Record<AssetFilter, number> = {
    all: assets.length,
    elegivel: 0,
    restrito: 0,
    inelegivel: 0,
    vendido: 0,
  };
  for (const asset of assets) counts[asset.eligibility]++;
  return counts;
}

/**
 * Pontos exibidos de saída: os que o backend marcou como `displayed` e não foram dispensados. A lista completa
 * ("Ver todos os achados") mostra todos, inclusive os dispensados, que aparecem riscados com o motivo.
 */
export function visibleFindings(findings: readonly FindingDto[], showAll: boolean): FindingDto[] {
  if (showAll) return [...findings];
  return findings.filter(finding => finding.displayed && !finding.dismissed);
}

/** Rótulo de uma declaração no histórico: "Original · 28/04/2026" é montado no template com o pipe de data. */
export function describeReturnKind(kind: ReturnSummaryDto['kind']): string {
  if (kind === 'retificadora') return 'Retificadora';
  if (kind === 'original') return 'Original';
  return 'Tipo não informado';
}

/**
 * Texto do status de uma declaração que ainda não está pronta. `null` quando já foi lida. A próxima declaração
 * ("awaiting") mostra o prazo de entrega do exercício (maio), para não parecer atraso do cliente.
 */
export function describeReturnStatus(
  status: ReturnSummaryDto['status'],
  exerciseYear?: number | null
): string | null {
  switch (status) {
    case 'awaiting':
      return exerciseYear
        ? `Próxima declaração · entrega até mai/${exerciseYear}`
        : 'Próxima declaração';
    case 'processing':
      return 'Lendo a declaração';
    case 'failed':
      return 'Não conseguimos ler';
    case 'needs-review':
      return 'Leitura a conferir';
    default:
      return null;
  }
}

function sortedUnique(values: readonly number[]): number[] {
  return [...new Set(values)].filter(Number.isFinite).sort((a, b) => a - b);
}

/**
 * Faixa da base: "IRPF 2024 a 2026 · saldos de 2022 a 2025".
 *
 * - `exerciseYears`: exercícios das declarações já lidas (status `extracted`).
 * - `balanceYears`: anos dos saldos em 31/12 exibidos nas tabelas (a `window` do sócio). O primeiro saldo pode vir
 *   só da coluna "ano anterior" da primeira declaração, por isso não dá para derivar um do outro.
 *
 * Sem exercícios (visão consolidada), mostra só os saldos. Nada nos dois, `null`.
 */
export function describeBaseRange(
  exerciseYears: readonly number[],
  balanceYears: readonly number[]
): string | null {
  const exercises = sortedUnique(exerciseYears);
  const balances = sortedUnique(balanceYears);
  const parts: string[] = [];

  if (exercises.length) {
    const first = exercises[0];
    const last = exercises[exercises.length - 1];
    parts.push(first === last ? `IRPF ${first}` : `IRPF ${first} a ${last}`);
  }
  if (balances.length) {
    const first = balances[0];
    const last = balances[balances.length - 1];
    parts.push(first === last ? `saldo de ${first}` : `saldos de ${first} a ${last}`);
  }

  if (!parts.length) return null;
  const label = parts.join(' · ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function normalizeName(text: string | null | undefined): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** Dívida ligada ao bem pelo backend: pelo id quando vier, senão pelo nome curto (o backend usa o do bem). */
export function isDebtSecuredBy(
  debt: { securedAssetId?: string | null; securedAssetName: string | null },
  asset: { id: string; shortName: string }
): boolean {
  if (debt.securedAssetId) return debt.securedAssetId === asset.id;
  const name = normalizeName(debt.securedAssetName);
  return !!name && name === normalizeName(asset.shortName);
}

/** Nome da dívida na tela: o nome curto do adendo v2 quando vier, senão o credor. */
export function debtDisplayName(debt: { shortName?: string | null; creditorName: string }): string {
  return debt.shortName?.trim() ? debt.shortName.trim() : formatAssetName(debt.creditorName);
}

/**
 * Notas do bem com as dívidas que ele garante (N3), mostradas mesmo sem política de garantias: "Alienado ao
 * financiamento de Caixa Econômica Federal: R$ 715 mil" (financiamento) ou "Garante a dívida com <credor>: R$ x"
 * (demais). O valor é o saldo da dívida no último ano da janela, como veio do backend.
 */
export function describeSecuringDebts(
  asset: { id: string; shortName: string },
  debts: readonly {
    securedAssetId?: string | null;
    securedAssetName: string | null;
    creditorName: string;
    purpose?: string | null;
    values: readonly { year: number; value: number | null }[];
  }[],
  lastYear: number | null
): string[] {
  return debts
    .filter(debt => isDebtSecuredBy(debt, asset))
    .map(debt => {
      const creditor = formatAssetName(debt.creditorName);
      const balance = lastYear == null ? null : valueForYear(debt.values, lastYear);
      const amount = balance != null && balance > 0 ? `: ${formatCompactMoney(balance)}` : '';
      const financing = (debt.purpose ?? '').startsWith('financiamento');
      return financing
        ? `Alienado ao financiamento de ${creditor}${amount}`
        : `Garante a dívida com ${creditor}${amount}`;
    });
}

/**
 * Pontos de uma linha simples (sparkline) num retângulo `width` × `height`, para o SVG do KPI. Só geometria de
 * desenho: os valores vêm prontos do backend. Menos de 2 pontos com valor, `null`.
 */
export function sparklinePoints(
  series: readonly { year: number; value: number | null }[],
  width: number,
  height: number,
  padding = 3
): {
  path: string;
  last: { x: number; y: number };
  /** Cada ponto com o ano e o valor, para os rótulos de valor da sparkline (R4-5). */
  points: { x: number; y: number; year: number; value: number }[];
} | null {
  const points = series.filter(
    (item): item is { year: number; value: number } =>
      item.value != null && Number.isFinite(item.value)
  );
  if (points.length < 2) return null;
  const values = points.map(item => item.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const step = (width - padding * 2) / (points.length - 1);
  const coords = points.map((item, index) => ({
    x: round(padding + index * step, 2),
    // Série sem variação fica no meio, não colada embaixo.
    y: span
      ? round(height - padding - ((item.value - min) / span) * (height - padding * 2), 2)
      : round(height / 2, 2),
  }));
  return {
    path: coords.map((c, index) => `${index ? 'L' : 'M'}${c.x} ${c.y}`).join(' '),
    last: coords[coords.length - 1],
    points: coords.map((c, index) => ({
      ...c,
      year: points[index].year,
      value: points[index].value,
    })),
  };
}

/** Valor de um ano numa série (`null` quando o ano não existe na série). */
export function valueForYear(
  values: readonly { year: number; value: number | null }[],
  year: number
): number | null {
  return values.find(item => item.year === year)?.value ?? null;
}
